import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const SLIDES = ["/hero-slide-1.jpg", "/hero-slide-2.jpg", "/hero-slide-3.jpg"];
const SLIDE_MS = 3000;

export const Hero = () => {
  const ref = useRef(null);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), SLIDE_MS);
    return () => clearInterval(t);
  }, []);

  // Scroll parallax
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.16]);

  // Subtle 3D tilt following the cursor
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [2.4, -2.4]), { stiffness: 55, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-3, 3]), { stiffness: 55, damping: 18 });

  const onMouseMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section className="relative bg-ivory flex justify-center px-0 pt-0 pb-0 sm:px-6 sm:py-6" data-testid="hero-section">
      <div
        ref={ref}
        onMouseMove={onMouseMove}
        className="relative w-full aspect-[4/5] sm:aspect-auto sm:h-[80vh] sm:w-[64vh] sm:max-w-full overflow-hidden bg-charcoal"
      >
      {/* Parallax + tilted slideshow */}
      <motion.div
        style={{ y: imgY, scale: imgScale, rotateX, rotateY, transformPerspective: 1200 }}
        className="absolute inset-0 will-change-transform"
      >
        <AnimatePresence mode="sync">
          <motion.img
            key={slide}
            src={SLIDES[slide]}
            alt="Authentic handloom Ilkal sarees by R V Ilkal Sarees"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: EASE }}
            className="absolute inset-0 w-full h-full object-cover object-center"
            data-testid="hero-slide-image"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent" />
        <div className="absolute inset-0 hero-grain pointer-events-none" />
      </motion.div>

      {/* Clipped gold frame — draws itself on load */}
      <motion.div
        initial={{ opacity: 0, scale: 1.015 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.6, delay: 0.9, ease: EASE }}
        className="absolute inset-3 sm:inset-5 border border-gold/40 pointer-events-none z-10"
      />

      {/* Action buttons */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.8, ease: EASE }}
        className="absolute z-20 bottom-14 sm:bottom-16 inset-x-0 flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-center gap-3 sm:gap-4 px-6 sm:px-8"
      >
        <Link
          to="/shop?category=traditional"
          data-testid="hero-shop-btn"
          className="group relative overflow-hidden bg-maroon text-ivory px-9 py-4 text-xs tracking-[0.22em] uppercase text-center transition-colors hover:bg-maroon-dark shadow-lg shadow-charcoal/40"
        >
          <span className="relative z-10">Shop Ilkal Sarees</span>
          <span className="absolute inset-0 -translate-x-full group-hover:translate-x-0 bg-gold/20 transition-transform duration-500 ease-out" />
        </Link>
        <Link
          to="/shop"
          data-testid="hero-explore-btn"
          className="border border-ivory/80 bg-charcoal/30 backdrop-blur-sm text-ivory px-9 py-4 text-xs tracking-[0.22em] uppercase text-center hover:bg-ivory hover:text-charcoal transition-colors"
        >
          Explore Collection
        </Link>
      </motion.div>

      {/* slide dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2.5" data-testid="hero-slide-dots">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setSlide(i)}
            aria-label={`Show banner ${i + 1}`}
            data-testid={`hero-dot-${i}`}
            className={`h-[3px] transition-all duration-500 ${i === slide ? "w-10 bg-gold" : "w-5 bg-ivory/40 hover:bg-ivory/70"}`}
          />
        ))}
      </div>
      </div>
    </section>
  );
};
